import React, { useEffect, useRef, useState } from 'react';
import { Image as KonvaImage } from 'react-konva';
import { observer } from 'mobx-react-lite';

const SVGElementComponent = observer(({ element, isSelected, onSelect, onTransformEnd, onDragEnd, groupChild }) => {
  const imageRef = useRef(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (!element.src) {
      setImage(null);
      return;
    }

    let svgSrc = element.src;

    // Apply color replacements to SVG
    if (element.colorsReplace && element.colorsReplace.size > 0) {
      // If it's a data URI SVG, decode and replace colors
      if (svgSrc.startsWith('data:image/svg+xml')) {
        try {
          let svgContent;
          if (svgSrc.includes('base64,')) {
            svgContent = atob(svgSrc.split('base64,')[1]);
          } else {
            svgContent = decodeURIComponent(svgSrc.split(',')[1]);
          }

          element.colorsReplace.forEach((newColor, oldColor) => {
            svgContent = svgContent.replace(new RegExp(oldColor, 'gi'), newColor);
          });

          svgSrc = 'data:image/svg+xml;base64,' + btoa(svgContent);
        } catch (e) {
          console.warn('Failed to process SVG color replacement', e);
        }
      }
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
    };
    img.onerror = () => {
      console.warn('Failed to load SVG:', element.src.substring(0, 100));
    };
    img.src = svgSrc;
  }, [element.src, element.colorsReplace?.toJSON?.()]);

  if (!element.visible) return null;

  const handleDragEnd = (e) => {
    element.set({
      x: e.target.x(),
      y: e.target.y(),
    });
    if (onDragEnd) onDragEnd(e);
  };

  const handleTransformEnd = (e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    element.set({
      x: node.x(),
      y: node.y(),
      width: Math.max(5, node.width() * scaleX),
      height: Math.max(5, node.height() * scaleY),
      rotation: node.rotation(),
    });

    if (onTransformEnd) onTransformEnd(e);
  };

  return (
    <KonvaImage
      ref={imageRef}
      id={element.id}
      name={element.name || 'svg'}
      image={image}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      rotation={element.rotation}
      opacity={element.opacity}
      draggable={!groupChild && element.draggable}
      visible={element.visible}
      scaleX={element.flipX ? -1 : 1}
      scaleY={element.flipY ? -1 : 1}
      offsetX={element.flipX ? element.width : 0}
      offsetY={element.flipY ? element.height : 0}
      cornerRadius={element.cornerRadius}
      shadowEnabled={element.shadowEnabled}
      shadowColor={element.shadowColor}
      shadowBlur={element.shadowBlur}
      shadowOffsetX={element.shadowOffsetX}
      shadowOffsetY={element.shadowOffsetY}
      shadowOpacity={element.shadowOpacity}
      onClick={(e) => {
        if (onSelect) onSelect(e);
      }}
      onTap={(e) => {
        if (onSelect) onSelect(e);
      }}
      onDragEnd={handleDragEnd}
      onTransformEnd={handleTransformEnd}
    />
  );
});

SVGElementComponent.displayName = 'SVGElement';
export default SVGElementComponent;
